
import {
  defineComponent,
  inject,
  PropType,
  reactive,
  ref,
  Ref,
  watchEffect,
} from "vue";
import { DialogGenre, RowId } from "@/interfaces/common.interface";
import { ElMessage } from "element-plus";
import { CheckMsg, InfoMsg } from "@/constants/message.constant";
import {
  checkKaishaUnyo,
  createGroup,
  getGroupById,
  updateGroup,
} from "@/api/kaisha.api";
import { GroupModel, KaishaInfo } from "@/interfaces/kaisha.interface";
import { dateFormat } from "@/utils/date";
import useSelectDialog from "@/hooks/useSelectDialog";
import Kaisha from "@/views/kaisha/index.vue";

export default defineComponent({
  props: {
    title: String as PropType<string>, //ダイアログタイトル
    operation: String as PropType<DialogGenre>, //ダイアログ機能（登録/修正）
  },
  components: {
    Kaisha,
  },
  emits: ["search"],
  async setup(props, { emit }) {
    //親からの変数
    const dialogFormVisible = inject("dialogFormVisible") as Ref<boolean>;
    const rowId = inject("rowId") as Ref<RowId>;

    const form = reactive({
      id: 0,
      groupId: "",
      groupNm: "",
      kaishaCd: "",
      kaishaNm: "",
      unyoNm: "",
      mukoDate: "",
      mukoFlg: false,
    } as GroupModel);
    const formRef = ref();
    const loading = ref(false);

    //入力情報のチェックルール
    const rules = {
      groupNm: [
        {
          required: true,
          trigger: "blur",
          message: CheckMsg.CHK_KAISHA_GROUP_NM2,
        },
      ],
      kaishaCd: [
        {
          required: true,
          validator: async (_: any, value: string, callback: any) => {
            if (props.operation === DialogGenre.Add) {
              if (!value) {
                callback(CheckMsg.CHK_KAISHA_CD);
              } else if (
                form.unyoNm === "集中型" &&
                (await checkKaishaUnyo(value))
              ) {
                callback(CheckMsg.CHK_KAISHA_UNYO);
              }
            }
          },
          trigger: "change",
        },
      ],
    };

    //フォームリセット
    const clearForm = () => {
      formRef.value.resetFields();
      form.kaishaNm = "";
      dialogFormVisible.value = false;
      rowId.value = undefined;
    };

    //登録/修正ボタンイベント
    const handleCreateOrUpdate = () => {
      formRef.value?.validate(async (valid: unknown) => {
        if (valid) {
          loading.value = true;
          try {
            if (props.operation === DialogGenre.Add) {
              await createGroup({
                kaishaCd: form.kaishaCd,
                groupNm: form.groupNm,
              });
              ElMessage({
                type: "success",
                message: InfoMsg.INFO_GROUP_ADDED,
              });
            } else {
              await updateGroup(form.id, {
                groupNm: form.groupNm,
                mukoFlg: form.mukoFlg,
              });
              ElMessage({
                type: "success",
                message: InfoMsg.INFO_GROUP_UPDATED,
              });
            }
            loading.value = false;
            clearForm();
            //再度検索
            emit("search");
          } catch (error) {
            loading.value = false;
          }
        } else {
          return false;
        }
      });
    };

    //修正モード時、選択行の情報取得
    const getGroupInfo = async (id: number) => {
      const {
        data: { result },
      } = await getGroupById(id);
      form.id = result.id;
      form.groupId = result.kaisha_group_id;
      form.groupNm = result.kaisha_group_nm;
      form.kaishaCd = result.kaisha.kaisha_cd;
      form.kaishaNm = result.kaisha.kaisha_nm;
      form.unyoNm = result.kaisha.unyo.unyo_kubun;
      form.mukoFlg = result.muko_flg;
      form.mukoDate = result.muko_flg ? dateFormat(result.muko_date) : "";
    };

    watchEffect(async () => {
      if (props.operation === DialogGenre.Edit && rowId.value) {
        await getGroupInfo(rowId.value as number);
      }
    });

    //会社選択画面用
    const { selectDialogVisible, handleSelect, handleCloseDialog } =
      useSelectDialog();

    const handleSelectInfo = (row: KaishaInfo) => {
      form.kaishaCd = row.kaishaCd;
      form.kaishaNm = row.kaishaNm;
      form.unyoNm = row.unyoNm;
      handleCloseDialog();
    };

    return {
      dialogFormVisible,
      form,
      formRef,
      loading,
      rules,
      DialogGenre,
      handleCreateOrUpdate,
      clearForm,
      selectDialogVisible,
      handleSelect,
      handleCloseDialog,
      handleSelectInfo,
    };
  },
});
